import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import { works } from "@/mocks/works";
import { useSeo } from "@/hooks/useSeo";
import type { Work } from "@/types";

export default function DetailPage() {
  const { workId } = useParams();
  const work = (works as Work[]).find((w) => w.id === workId);

  const [activeImage, setActiveImage] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [toast, setToast] = useState("");

  useSeo({
    title: work
      ? `${work.title} - 女性ins大片作品详情`
      : "作品未找到 - 女性ins大片",
    description: work
      ? `${work.title}：由摄影师${work.author}创作的${work.categoryName}作品，拍摄于${work.location}。${work.description}`
      : "抱歉，作品未找到",
    keywords: work
      ? `${work.title},${work.categoryName},女性ins大片,${work.tags.join(",")},女性摄影作品`
      : "女性ins大片",
    path: `/detail/${workId}`,
  });

  useEffect(() => {
    setActiveImage(0);
    setLightbox(false);
  }, [workId]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  if (!work) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <SiteNavbar />
        <main className="flex-1 flex flex-col items-center justify-center px-4 pt-16 text-center">
          <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
            <i className="ri-image-2-line text-3xl text-foreground-400"></i>
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            抱歉，作品未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-7">该作品可能已被移除或链接有误</p>
          <Link
            to="/"
            className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = (works as Work[])
    .filter((w) => w.id !== work.id && (w.categoryId === work.categoryId || w.tags.some((t) => work.tags.includes(t))))
    .slice(0, 4);

  const gallery = work.gallery.length > 0 ? work.gallery : [work.cover];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("链接复制失败，请手动复制");
    }
  };

  const metaItems = [
    { icon: "ri-user-line", label: "摄影师", value: work.author },
    { icon: "ri-calendar-line", label: "拍摄时间", value: work.date },
    { icon: "ri-price-tag-3-line", label: "风格分类", value: work.categoryName },
    { icon: "ri-map-pin-line", label: "拍摄地点", value: work.location },
    { icon: "ri-eye-line", label: "浏览量", value: work.views.toLocaleString() },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteNavbar />
      <main className="pt-16 md:pt-[72px]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${work.categoryId}`} className="hover:text-primary-600 cursor-pointer">
              {work.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{work.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Images */}
            <div className="lg:col-span-3">
              <div
                className="relative w-full aspect-[4/5] md:aspect-[3/2] rounded-lg overflow-hidden bg-background-100 cursor-zoom-in group"
                onClick={() => setLightbox(true)}
              >
                <img
                  src={gallery[activeImage]}
                  alt={`${work.title} - 女性ins大片高清大图`}
                  title={`${work.title} - 时尚女性摄影作品`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-foreground-950/60 text-background-50 text-xs flex items-center gap-1">
                  <i className="ri-zoom-in-line"></i>
                  点击放大
                </span>
              </div>
              {gallery.length > 1 && (
                <div className="grid grid-cols-4 gap-3 mt-3">
                  {gallery.map((src, i) => (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setActiveImage(i)}
                      className={`relative w-full aspect-square rounded-md overflow-hidden cursor-pointer border-2 transition-colors ${
                        i === activeImage ? "border-primary-500" : "border-transparent"
                      }`}
                    >
                      <img
                        src={src}
                        alt={`${work.title} 作品图 ${i + 1}`}
                        title={`${work.title} - 摄影作品图`}
                        className="w-full h-full object-cover object-top"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                <i className="ri-price-tag-3-line"></i>
                {work.categoryName}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                {work.title}
              </h1>

              <div className="grid grid-cols-2 gap-4 py-5 border-y border-background-200/70 mb-5">
                {metaItems.map((m) => (
                  <div key={m.label}>
                    <p className="text-xs text-foreground-400 mb-1 flex items-center gap-1">
                      <i className={m.icon}></i>
                      {m.label}
                    </p>
                    <p className="text-sm font-medium text-foreground-800">{m.value}</p>
                  </div>
                ))}
              </div>

              <p className="font-label text-xs font-medium text-foreground-500 mb-2">创作说明</p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                {work.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-7">
                {work.tags.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                  >
                    #{t}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-7">
                <Link
                  to={`/category/${work.categoryId}`}
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-grid-line"></i>
                  浏览同分类
                </Link>
                <button
                  type="button"
                  onClick={copyLink}
                  className="px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-link"></i>
                  复制链接
                </button>
              </div>

              <div className="bg-secondary-50 rounded-lg p-5">
                <p className="font-label text-xs font-medium text-foreground-500 mb-3">分享作品</p>
                <div className="flex items-center gap-3">
                  {[
                    { icon: "ri-weibo-line", label: "微博" },
                    { icon: "ri-wechat-line", label: "微信" },
                    { icon: "ri-instagram-line", label: "Instagram" },
                    { icon: "ri-pinterest-line", label: "Pinterest" },
                  ].map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => setToast(`已打开${s.label}分享（演示）`)}
                      aria-label={`分享到${s.label}`}
                      className="w-10 h-10 rounded-full bg-background-50 border border-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-colors cursor-pointer"
                    >
                      <i className={`${s.icon} text-base`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <section className="mt-14 md:mt-20">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
                    Related
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
                    相关推荐作品
                  </h2>
                </div>
                <Link
                  to={`/category/${work.categoryId}`}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  更多{work.categoryName}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {related.map((w, i) => (
                  <WorkCard key={w.id} work={w} index={i} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[80] bg-foreground-950/90 flex items-center justify-center p-4 animate-fade-in">
          <button
            type="button"
            aria-label="关闭"
            onClick={() => setLightbox(false)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-background-50/15 text-background-50 flex items-center justify-center cursor-pointer hover:bg-background-50/25"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={gallery[activeImage]}
            alt={`${work.title} - 大图预览`}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[90] px-5 py-3 rounded-md bg-foreground-900 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}